import type {
  YachtCategory,
  YachtViewerState,
  YachtViewerTurnState,
} from "@cloudnest/yacht-engine";
import { useEffect, useMemo, useState } from "react";
import { ActionDock } from "../components/ActionDock";
import { GameResultPanel } from "../components/GameResultPanel";
import { PlayerScoreRail } from "../components/PlayerScoreRail";
import { ScoreBoardMatrix } from "../components/ScoreBoardMatrix";
import { ScoreSheetGrid } from "../components/ScoreSheetGrid";

function PlayerTabs({
  state,
  selectedPlayerId,
  onSelectPlayer,
}: {
  readonly state: YachtViewerState;
  readonly selectedPlayerId: string;
  readonly onSelectPlayer: (playerId: string) => void;
}) {
  return (
    <section className="yacht-player-tabs" data-testid="yacht-player-tabs">
      {state.seatingOrder.map((playerId) => {
        const player = state.players.find(
          (candidate) => candidate.playerId === playerId,
        );
        const slot = state.turnSlotsByPlayerId[playerId];
        const sheet = state.scoreSheetsByPlayerId[playerId];
        const recentlyScored = state.lastScoreResult?.playerId === playerId;
        return (
          <button
            className="yacht-player-tab"
            data-selected={selectedPlayerId === playerId ? "true" : "false"}
            data-status={
              slot?.status === "OPEN"
                ? "active"
                : slot?.status === "LOCKED"
                  ? "done"
                  : recentlyScored
                    ? "done"
                    : "idle"
            }
            key={playerId}
            onClick={() => onSelectPlayer(playerId)}
            title={player?.displayName ?? playerId}
            type="button"
          >
            <span>{player?.displayName ?? playerId}</span>
            <strong>{sheet?.total ?? 0}</strong>
          </button>
        );
      })}
    </section>
  );
}

function resolveInitialSelectedPlayerId(
  state: YachtViewerState,
  myPlayerId: string | null,
): string {
  if (state.viewerPlayerId) return state.viewerPlayerId;
  if (myPlayerId && state.seatingOrder.includes(myPlayerId)) return myPlayerId;
  return state.seatingOrder[0] ?? "";
}

export function MobileYachtScreen({
  state,
  myPlayerId,
  readOnly,
  onRoll,
  onToggleHold,
  onConfirmScore,
  onLeaveGame,
}: {
  readonly state: YachtViewerState;
  readonly myPlayerId: string | null;
  readonly readOnly: boolean;
  readonly onRoll: (slot: YachtViewerTurnState) => void;
  readonly onToggleHold: (slot: YachtViewerTurnState, dieIndex: number) => void;
  readonly onConfirmScore: (
    slot: YachtViewerTurnState,
    category: YachtCategory,
  ) => void;
  readonly onLeaveGame: () => void;
}) {
  const initialSelectedPlayerId = resolveInitialSelectedPlayerId(
    state,
    myPlayerId,
  );
  const [selectedPlayerId, setSelectedPlayerId] = useState(
    initialSelectedPlayerId,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<YachtCategory | null>(null);
  const viewerSlot = state.viewerPlayerId
    ? state.turnSlotsByPlayerId[state.viewerPlayerId]
    : undefined;
  const viewerWindowKey = viewerSlot
    ? `${viewerSlot.playerId}:${viewerSlot.sheetRound}:${viewerSlot.turnNumber}:${viewerSlot.rollCount}`
    : "none";

  useEffect(() => {
    if (!state.seatingOrder.includes(selectedPlayerId)) {
      setSelectedPlayerId(initialSelectedPlayerId);
    }
  }, [initialSelectedPlayerId, selectedPlayerId, state.seatingOrder]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the transient score selection whenever the viewer's active turn window or last confirmed score changes.
  useEffect(() => {
    setSelectedCategory(null);
  }, [viewerWindowKey, state.lastScoreResult?.turnNumber]);

  const selectedPreview = useMemo(
    () =>
      state.legalActions.scoreableCategories.find(
        (preview) => preview.category === selectedCategory,
      ) ?? null,
    [selectedCategory, state.legalActions.scoreableCategories],
  );
  const boardMode =
    state.turnMode === "SEQUENTIAL" && state.seatingOrder.length <= 6;
  const showDetail = !boardMode || detailOpen;

  function selectPlayerDetail(playerId: string): void {
    setSelectedPlayerId(playerId);
    setDetailOpen(true);
  }

  function selectCategory(category: YachtCategory): void {
    if (state.viewerPlayerId !== null) {
      setSelectedCategory((current) =>
        current === category ? null : category,
      );
    }
  }

  return (
    <main className="yacht-root" data-testid="yacht-game-screen">
      <div className="yacht-shell">
        <PlayerScoreRail
          state={state}
          selectedPlayerId={selectedPlayerId}
          onSelectPlayer={selectPlayerDetail}
        />
        <PlayerTabs
          state={state}
          selectedPlayerId={selectedPlayerId}
          onSelectPlayer={selectPlayerDetail}
        />
        {readOnly && state.viewerPlayerId === null && (
          <div
            className="yacht-observer-banner"
            data-testid="observer-mode-banner"
          >
            OBSERVER
          </div>
        )}
        {showDetail ? (
          <>
            {boardMode && (
              <div className="yacht-detail-toolbar">
                <button
                  className="yacht-detail-back"
                  onClick={() => setDetailOpen(false)}
                  type="button"
                >
                  전체 점수
                </button>
                <span>
                  {state.players.find(
                    (player) => player.playerId === selectedPlayerId,
                  )?.displayName ?? selectedPlayerId}
                </span>
              </div>
            )}
            <ScoreSheetGrid
              state={state}
              selectedCategory={selectedPreview?.category ?? null}
              selectedPlayerId={selectedPlayerId}
              onSelectCategory={(category) => {
                if (selectedPlayerId === state.viewerPlayerId) {
                  selectCategory(category);
                }
              }}
            />
          </>
        ) : (
          <ScoreBoardMatrix
            state={state}
            selectedCategory={selectedPreview?.category ?? null}
            onSelectCategory={selectCategory}
            onOpenPlayerDetail={selectPlayerDetail}
          />
        )}
        <GameResultPanel state={state} onLeaveGame={onLeaveGame} />
      </div>
      <ActionDock
        state={state}
        readOnly={readOnly}
        selectedCategory={selectedPreview?.category ?? null}
        onRoll={onRoll}
        onToggleHold={onToggleHold}
        onConfirmScore={onConfirmScore}
        onClearSelectedCategory={() => setSelectedCategory(null)}
      />
    </main>
  );
}
